'use client';

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSessionStore } from '@/store/sessionStore';
import {
  indexingStatusForProjectStatus,
  isProjectIndexingStatus,
  useIndexingStore,
} from '@/store/indexingStore';
import { useQuestionnaireRunStore } from '@/store/questionnaireRunStore';
import { api, translateBackendCode } from '@/shared/lib/api';
import type { WSMessage } from '@/shared/lib/types';

// Traduz a mensagem de uma mensagem WebSocket: se o backend enviou um `code`
// (+ `params`), traduz no idioma ativo; caso contrário usa o texto cru de fallback
// (nomes de ficheiro, contagens, ou mensagens ainda não migradas).
function translateWsData(data: any, fallback?: string): string {
  if (data && data.code) {
    return translateBackendCode(data.code, data.params || {});
  }
  return fallback ?? '';
}

interface WebSocketContextValue {
  isConnected: boolean;
  addListener: (listener: (message: WSMessage) => void) => () => void;
  /** Incrementa a cada ligação estabelecida DEPOIS da primeira, ou seja, a
   * cada recuperação de uma queda. Enquanto o socket esteve em baixo, as
   * mensagens que o backend publicou perderam-se para sempre (a entrega é
   * fire-and-forget, sem replay — ver `WebSocketManager.send_to_users`), por
   * isso quem mantém estado vindo do WebSocket tem de o ir buscar outra vez
   * ao servidor quando isto muda. 0 = ainda não houve nenhuma reconexão. */
  reconnectEpoch: number;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext deve ser usado dentro de um WebSocketProvider');
  }
  return context;
}

// Ligação WebSocket única, partilhada por toda a app: montada uma vez em
// AppShell. Faz as atualizações de estado incondicionais (stores globais) e
// distribui cada mensagem pelos listeners registados via `addListener`, para
// que várias páginas/hooks possam reagir a eventos sem cada uma abrir o seu
// próprio socket.
export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const sessionId = useSessionStore((s) => s.sessionId);
  const token = useSessionStore((s) => s.token);
  const updateIndexingStatus = useIndexingStore((s) => s.updateStatus);
  const updateProjectIndexingStatus = useIndexingStore((s) => s.updateProjectStatus);
  const { updateProgress: updateQProgress, completeRun, completeRunAll, failRun } = useQuestionnaireRunStore();

  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectEpoch, setReconnectEpoch] = useState(0);
  // Distingue a primeira ligação de uma reconexão: na primeira não há nada a
  // reconciliar (quem monta já faz o seu fetch inicial).
  const hasConnectedBeforeRef = useRef(false);

  const listenersRef = useRef<Set<(message: WSMessage) => void>>(new Set());

  // O refresh proativo da sessão (ver useLoginLayout) troca o `token` no store bem
  // antes de expirar, sem que a ligação atual deixe de ser válida — o token só é
  // verificado no handshake inicial (ver websocket.py). Mantemos o valor mais
  // recente numa ref, em vez de o `connect` depender de `token` diretamente, para
  // um refresh não forçar o fecho/reabertura de um WebSocket já ligado; a próxima
  // ligação (montagem ou reconexão após queda) usa sempre o token atual.
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const addListener = useCallback((listener: (message: WSMessage) => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  // `connect` só é recriado quando sessionId/token mudam (ver deps abaixo), pelo
  // que o `ws.onmessage` instalado por ele fica preso à versão de `handleMessage`
  // que existia nesse momento — por isso passa sempre pela ref, nunca invocando
  // `handleMessage` diretamente.
  const handleMessageRef = useRef<(message: WSMessage) => void>(() => {});

  const connect = useCallback(() => {
    if (!sessionId || wsRef.current?.readyState === WebSocket.OPEN) return;

    // Same-origin por defeito: herda host e protocolo da pagina (ws:// em http, wss:// em https).
    // Assim a imagem nao fica acoplada a um FQDN e nunca ha mixed-content quando a pagina e HTTPS.
    // NEXT_PUBLIC_WS_URL continua a funcionar como override explicito se for definido no build.
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsBaseUrl = process.env.NEXT_PUBLIC_WS_URL?.trim() || `${proto}//${window.location.host}`;
    // O handshake do WebSocket não permite headers custom no browser, por isso o JWT
    // de sessão vai como query param (validado + cruzado com o session_id no backend).
    const tokenQuery = tokenRef.current ? `?token=${encodeURIComponent(tokenRef.current)}` : '';
    const wsUrl = `${wsBaseUrl}/ws/${sessionId}${tokenQuery}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected');
      reconnectAttemptsRef.current = 0;
      setIsConnected(true);
      if (hasConnectedBeforeRef.current) {
        setReconnectEpoch((epoch) => epoch + 1);
      } else {
        hasConnectedBeforeRef.current = true;
      }
    };

    ws.onmessage = (event) => {
      try {
        const message: WSMessage = JSON.parse(event.data);
        console.log('WS message received:', message.type, message.data);
        handleMessageRef.current(message);
      } catch (e) {
        // Handle pong or non-JSON messages
        if (event.data !== 'pong') {
          console.log('WS non-JSON message:', event.data);
        }
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Reconectar automaticamente com backoff exponencial (máx. 15s)
      if (shouldReconnectRef.current) {
        const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 15000);
        reconnectAttemptsRef.current += 1;
        console.log(`WebSocket: reconnecting in ${delay}ms`);
        reconnectTimeoutRef.current = setTimeout(() => connect(), delay);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    wsRef.current = ws;

    // Ping interval to keep connection alive
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    pingIntervalRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      }
    }, 30000);
  }, [sessionId]);

  const handleMessage = useCallback(
    (message: WSMessage) => {
      switch (message.type) {
        case 'connected':
          console.log('Session connected:', message.data);
          break;

        case 'indexing_progress': {
          // O backend envia sempre `code` (+ `params`) para tradução.
          const progressText = translateWsData(message.data);
          if (message.project_id) {
            updateProjectIndexingStatus(message.project_id, {
              status: message.data.stage,
              progress_percent: message.data.percent,
              current_document: progressText,
              // Sempre presente (a `null` quando a mensagem não o traz): o
              // `openCount` descreve ESTA mensagem, não é estado acumulado —
              // herdar o valor anterior faria uma mensagem sem contagem ser
              // julgada pelo eco que veio antes dela.
              openCount: message.data.open_count ?? null,
              ...(message.data.project_name ? { projectName: message.data.project_name } : {}),
            });
          } else {
            updateIndexingStatus({
              status: message.data.stage,
              progress_percent: message.data.percent,
              current_document: progressText,
            });
          }
          break;
        }

        case 'indexing_complete':
          if (message.project_id) {
            updateProjectIndexingStatus(message.project_id, {
              status: 'completed',
              progress_percent: 100,
              total_documents: message.data.total_documents,
              ...(message.data.project_name ? { projectName: message.data.project_name } : {}),
            });
          } else {
            updateIndexingStatus({
              status: 'completed',
              progress_percent: 100,
              total_documents: message.data.total_documents,
            });
          }
          break;

        case 'indexing_batch_complete':
          // Sem estado próprio na store global: só serve de sinal para
          // useFilesPage recarregar a listagem (ver listenersRef abaixo).
          break;

        case 'indexing_error': {
          const errorText = translateWsData(message.data, message.data.error);
          if (message.project_id) {
            updateProjectIndexingStatus(message.project_id, {
              status: 'error',
              error_message: errorText,
              ...(message.data.project_name ? { projectName: message.data.project_name } : {}),
            });
          } else {
            updateIndexingStatus({
              status: 'error',
              error_message: errorText,
            });
          }
          break;
        }

        case 'questionnaire_progress':
          if (!message.project_id) break;
          updateQProgress({
            project_id: message.project_id,
            questionnaire_id: message.data.questionnaire_id,
            percent: message.data.percent,
            message: translateWsData(message.data),
            code: message.data.code,
            phase: message.data.phase,
            open_count: message.data.open_count,
            open_count_fresh: message.data.open_count_fresh,
            current_question: message.data.current_question,
            total_questions: message.data.total_questions,
            project_name: message.data.project_name,
          });
          break;

        case 'questionnaire_complete':
          if (!message.project_id) break;
          completeRun(
            message.project_id,
            message.data.project_name,
            message.data.had_failure,
            message.data.partial,
            message.data.phase,
            message.data.questionnaire_id,
          );
          break;

        case 'questionnaire_run_all_complete':
          if (!message.project_id) break;
          completeRunAll(message.project_id, message.data.project_name);
          break;

        case 'questionnaire_error':
          if (!message.project_id) break;
          failRun(
            message.project_id,
            translateWsData(message.data, message.data.error),
            message.data.project_name,
            message.data.questionnaire_id,
          );
          break;

        case 'query_start':
          console.log('Query started:', message.data.question);
          break;

        case 'query_complete':
          console.log('Query completed:', message.data);
          break;

        case 'project_status_updated': {
          // Rede de segurança para a barra de indexação: o fim de uma
          // indexação nem sempre chega como `indexing_complete`. Um projeto
          // que fecha em INDEXING_ERROR só emite este evento (o
          // `indexing_error` saiu do lote que falhou, possivelmente muito
          // antes e noutra réplica), e o mesmo vale para o fim de um
          // cancelamento — ver `reconcile_project_after_indexing`. Sem isto,
          // a entrada ficava a girar a 100% no ProgressToast até o
          // utilizador fazer refresh à página.
          if (!message.project_id) break;
          const projectStatus = message.data.status;
          if (isProjectIndexingStatus(projectStatus)) break;
          // Só fecha o que está aberto: sem esta guarda, qualquer mudança de
          // estado de qualquer projeto criava uma entrada no store para um
          // projeto que nunca chegou a indexar neste cliente.
          if (!useIndexingStore.getState().getProjectStatus(message.project_id).isIndexing) break;
          updateProjectIndexingStatus(message.project_id, {
            status: indexingStatusForProjectStatus(projectStatus),
            ...(message.data.project_name ? { projectName: message.data.project_name } : {}),
          });
          break;
        }

        default:
          console.log('Unknown WS message:', message);
      }

      // Distribuir a mensagem pelos subscritores (useWebSocket em cada
      // página/hook), independentemente do processamento incondicional acima.
      listenersRef.current.forEach((listener) => listener(message));
    },
    [
      updateIndexingStatus,
      updateProjectIndexingStatus,
      updateQProgress,
      completeRun,
      completeRunAll,
      failRun,
    ],
  );

  useEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  // Reconciliação da barra de progresso depois de uma reconexão: o fim da
  // indexação pode ter sido anunciado enquanto o socket estava em baixo, e
  // essa mensagem não volta (ver `reconnectEpoch`). Vai buscar ao servidor o
  // estado real de cada projeto que este cliente ainda julga estar a indexar
  // e fecha os que já terminaram — sem isto, a barra ficava a 100% "a
  // processar" até um refresh à página.
  //
  // Aqui, e não nas páginas, porque a entrada do ProgressToast existe
  // independentemente da página aberta: as páginas recarregam os SEUS dados
  // pelo `onReconnected` do useWebSocket.
  useEffect(() => {
    if (reconnectEpoch === 0) return;

    const indexingProjectIds = Object.entries(useIndexingStore.getState().projectStatusById)
      .filter(([, state]) => state.isIndexing)
      .map(([projectId]) => projectId);
    if (indexingProjectIds.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const projectId of indexingProjectIds) {
        try {
          const project = await api.getProject(projectId);
          if (cancelled) return;
          // Adota o estado do servidor tal e qual: fecha a barra se a
          // indexação já acabou, e corrige percentagem/mensagem se continua a
          // decorrer (o que se perdeu enquanto o socket esteve em baixo).
          useIndexingStore.getState().syncProjectFromServer(project);
        } catch {
          // Best-effort: se o projeto já não existir, ou a rede ainda estiver
          // instável, fica como está — a próxima mensagem de progresso ou a
          // reconexão seguinte voltam a resolver.
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reconnectEpoch, updateProjectIndexingStatus]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connect();
    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      wsRef.current?.close();
    };
  }, [connect]);

  return (
    <WebSocketContext.Provider value={{ isConnected, addListener, reconnectEpoch }}>
      {children}
    </WebSocketContext.Provider>
  );
}
